<!-- src/apps/secret/reveal/ShowReceipt.vue -->

<script setup lang="ts">
  import BurnButtonForm from '@/apps/secret/components/receipt/BurnButtonForm.vue';
  import ExpiryRing from '@/apps/secret/components/receipt/ExpiryRing.vue';
  import ReceiptFAQ from '@/apps/secret/components/receipt/ReceiptFAQ.vue';
  import SecretLink from '@/apps/secret/components/receipt/SecretLink.vue';
  import ShareChannels from '@/apps/secret/components/receipt/ShareChannels.vue';
  import StatusBadge from '@/apps/secret/components/receipt/StatusBadge.vue';
  import TimelineDisplay from '@/apps/secret/components/receipt/TimelineDisplay.vue';
  import { peekAttachmentFragment } from '@/apps/secret/composables/useAttachmentFragmentStore';
  import ReceiptSkeleton from '@/shared/components/closet/ReceiptSkeleton.vue';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import NeedHelpModal from '@/shared/components/modals/NeedHelpModal.vue';
  import CopyButton from '@/shared/components/ui/CopyButton.vue';
  import CornerBrackets from '@/shared/components/ui/CornerBrackets.vue';
  import { useReceipt } from '@/shared/composables/useReceipt';
  import { useSecretExpiration, EXPIRATION_EVENTS } from '@/shared/composables/useSecretExpiration';
  import { useAuthStore } from '@/shared/stores/authStore';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { copyToClipboard } from '@/utils/clipboard';
  import { formatDisplayDateTime } from '@/utils/format';
  import { storeToRefs } from 'pinia';
  import { onMounted, onUnmounted, watch, computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import UnknownReceipt from './UnknownReceipt.vue';

  // Define props
  interface Props {
    receiptIdentifier: string;
  }
  const props = defineProps<Props>();

  const { t } = useI18n();

  // State for delayed warning message
  const showWarning = ref(false);
  const warningMessage = ref<HTMLElement | null>(null);

  const { record, details, isLoading, fetch, reset } = useReceipt(props.receiptIdentifier);

  const { onExpirationEvent } = useSecretExpiration(
    record.value?.created ?? new Date(),
    record.value?.expiration_in_seconds ?? 0
  );

  const isAvailable = computed(() => !(record.value?.is_destroyed || record.value?.is_burned || record.value?.is_revealed));

  // The receipt-page Burn action is shown unless ui.capabilities.burn is
  // explicitly disabled. An unset flag (undefined) is treated as enabled,
  // matching the config default of true.
  const bootstrapStore = useBootstrapStore();
  const authStore = useAuthStore();
  // Server-side email delivery (POST /receipt/:id/notify) requires an
  // authenticated owner; guests fall back to the system mail composer.
  const isAuthenticated = computed(() => authStore.isAuthenticated === true);
  const { uiCapabilities } = storeToRefs(bootstrapStore);
  const showBurn = computed(() => uiCapabilities.value?.burn !== false);
  const helpEnabled = computed(() => bootstrapStore.ui?.help?.enabled ?? true);

  // ── Terminal-receipt derived state (Figma Make port) ───────────────────────

  // Same URL source SecretLink always used: the API-provided share_url — but
  // when the creator attached E2EE files, append the id→key fragment stashed at
  // creation time (the keys live only in this tab, never on the server). The
  // recipient's reveal page reads that fragment to download + decrypt.
  const attachmentFragment = ref(peekAttachmentFragment(props.receiptIdentifier));
  const shareUrl = computed(() => {
    const base = record.value?.share_url ?? '';
    if (!base || !attachmentFragment.value) return base;
    return `${base}#${attachmentFragment.value}`;
  });
  // Name-only attachment list for the receipt view (no download here).
  const attachments = computed(
    () => (record.value?.attachments ?? []) as Array<{ filename: string; size?: number }>
  );

  // The big copyable link hero replaces SecretLink's textarea except in the
  // recipient flow (show_recipients), where the link is intentionally hidden
  // and SecretLink keeps its short-id/passphrase presentation.
  const showHero = computed(
    () => isAvailable.value && !!shareUrl.value && !details.value?.show_recipients
  );

  // Timestamps arrive as Date objects (v3 schema transforms). Guard with
  // instanceof so partial records (tests, legacy payloads) can't crash the UI.
  const createdAt = computed(() =>
    record.value?.created instanceof Date ? record.value.created : null
  );
  const expiresAt = computed(() => {
    const r = record.value;
    if (!r) return null;
    if (r.expiration instanceof Date) return r.expiration;
    if (createdAt.value && r.expiration_in_seconds) {
      return new Date(createdAt.value.getTime() + r.expiration_in_seconds * 1000);
    }
    return null;
  });

  // Status-strip pill: real record state, "active" only while still shareable.
  const statusPill = computed(() => {
    const r = record.value;
    if (!r) return null;
    if (r.is_burned) {
      return {
        label: t('web.STATUS.burned'),
        dot: 'bg-amber-500',
        text: 'text-amber-600 dark:text-amber-400',
      };
    }
    if (r.is_revealed) {
      return {
        label: t('web.STATUS.received'),
        dot: 'bg-emerald-500',
        text: 'text-emerald-600 dark:text-emerald-400',
      };
    }
    if (r.is_destroyed) {
      return {
        label: t('web.STATUS.expired'),
        dot: 'bg-gray-400',
        text: 'text-day-ink-faint dark:text-night-ink-faint',
      };
    }
    return {
      label: t('receipt_terminal.status_active'),
      dot: 'bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]',
      text: 'text-brand-600 dark:text-brand-500',
    };
  });

  // Metadata strip cells (only render what the record actually provides).
  const metaCells = computed(() => {
    const cells: { key: string; label: string; value: string }[] = [];
    if (createdAt.value) {
      cells.push({
        key: 'created',
        label: t('receipt_terminal.meta_created'),
        value: formatDisplayDateTime(createdAt.value),
      });
    }
    if (expiresAt.value) {
      cells.push({
        key: 'expires',
        label: t('receipt_terminal.meta_expires'),
        value: formatDisplayDateTime(expiresAt.value),
      });
    }
    cells.push({ key: 'cipher', label: t('receipt_terminal.meta_cipher'), value: 'AES-256' });
    if (attachments.value.length) {
      cells.push({
        key: 'files',
        label: t('receipt_terminal.meta_files'),
        value: attachments.value.map((a) => a.filename).join(', '),
      });
    }
    if (record.value?.secret_shortid) {
      cells.push({
        key: 'shortid',
        label: t('receipt_terminal.meta_secret_id'),
        value: record.value.secret_shortid,
      });
    }
    return cells;
  });

  // Click-to-copy state for the link hero.
  const linkCopied = ref(false);
  let linkCopiedTimer: ReturnType<typeof setTimeout> | undefined;

  const copyLink = async () => {
    if (!shareUrl.value) return;
    const ok = await copyToClipboard(shareUrl.value);
    if (!ok) return;
    linkCopied.value = true;
    if (linkCopiedTimer) clearTimeout(linkCopiedTimer);
    linkCopiedTimer = setTimeout(() => {
      linkCopied.value = false;
    }, 2000);
  };

  const router = useRouter();

  // Deterministic same-origin destination instead of window.history.back().
  // A blind history replay can leave the app domain entirely — e.g. back to a
  // secret link previously opened in this tab on the link domain (seuid.online).
  // Authenticated owners return to their Dashboard; guests to the homepage.
  const goBack = () => {
    router.push(isAuthenticated.value ? { name: 'Dashboard' } : { path: '/' });
  };

  // Watch for route parameter changes to refetch data
  watch(
    () => props.receiptIdentifier,
    (newKey) => {
      reset();
      if (newKey) {
        fetch();
      }
    }
  );

  onMounted(() => {
    fetch();

    // Handle expiration events at page level
    onExpirationEvent(EXPIRATION_EVENTS.EXPIRED, () => {
      // Update UI state, show notifications etc
    });

    onExpirationEvent(EXPIRATION_EVENTS.WARNING, () => {
      // Show warning notification
    });

    // Delay showing the warning message for better screen reader experience
    // This ensures the page has loaded and user has context before the warning is announced
    setTimeout(() => {
      showWarning.value = true;
    }, 1500); // 1.5 second delay for optimal timing
  });

  // Ensure cleanup when component unmounts
  onUnmounted(() => {
    reset();
    if (linkCopiedTimer) clearTimeout(linkCopiedTimer);
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="container mx-auto px-4">
      <!--  Add Back navigation link -->
      <!-- prettier-ignore-attribute class -->
      <button
        type="button"
        @click="goBack"
        class="mt-2 mb-4 inline-flex items-center gap-2 font-mono text-sm
        text-day-ink-muted hover:text-day-ink
        dark:text-night-ink-muted dark:hover:text-night-ink">
        <OIcon
          collection="heroicons"
          name="arrow-left"
          size="5" />
        {{ t('web.COMMON.back') }}
      </button>

      <ReceiptSkeleton v-if="isLoading" />

      <div v-else-if="!record || !details">
        <UnknownReceipt />
      </div>

      <div
        v-else-if="record && details"
        class="mx-auto max-w-4xl space-y-10 pb-10">
        <!-- Terminal panel (Figma Make receipt chrome) -->
        <!-- prettier-ignore-attribute class -->
        <div
          class="relative border border-day-line bg-day
          dark:border-white/[0.07] dark:bg-night-100">
          <!-- Status strip -->
          <!-- prettier-ignore-attribute class -->
          <div
            data-testid="receipt-status-strip"
            class="flex h-9 items-center justify-between gap-4 overflow-x-auto
            border-b border-day-line bg-day-200 px-3.5
            dark:border-white/[0.13] dark:bg-night-200">
            <div class="flex shrink-0 items-center gap-4">
              <span
                v-if="statusPill"
                class="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.08em] uppercase"
                :class="statusPill.text">
                <span
                  class="inline-block size-[5px] shrink-0 rounded-full"
                  :class="statusPill.dot"
                  aria-hidden="true"></span>
                {{ statusPill.label }}
              </span>
              <!-- prettier-ignore-attribute class -->
              <span
                class="font-mono text-[9px] tracking-[0.08em] text-day-ink-faint
                uppercase dark:text-night-ink-faint">
                AES-256
              </span>
              <!-- prettier-ignore-attribute class -->
              <span
                v-if="isAvailable"
                class="font-mono text-[9px] tracking-[0.08em] text-day-ink-faint
                uppercase dark:text-night-ink-faint">
                {{ t('receipt_terminal.reads_remaining') }}
              </span>
            </div>
            <!-- prettier-ignore-attribute class -->
            <span
              v-if="details.has_passphrase"
              class="flex shrink-0 items-center gap-1.5 font-mono text-[9px] tracking-[0.08em]
              text-emerald-600 uppercase dark:text-emerald-400">
              <span
                class="inline-block size-[5px] shrink-0 rounded-full bg-[#00C47A] shadow-[0_0_6px_#00C47A]"
                aria-hidden="true"></span>
              {{ t('receipt_terminal.passphrase_active') }}
            </span>
          </div>

          <!-- Subject strip: the creator-supplied subject, to identify the
               message. Shown just under the status strip when present. -->
          <!-- prettier-ignore-attribute class -->
          <div
            v-if="record.subject"
            data-testid="receipt-subject"
            class="flex items-center gap-2 border-b border-day-line bg-day px-3.5 py-2
            dark:border-white/[0.07] dark:bg-night-100">
            <!-- prettier-ignore-attribute class -->
            <span
              class="shrink-0 font-mono text-[9px] tracking-[0.08em] text-day-ink-faint
              uppercase dark:text-night-ink-faint">
              {{ t('receipt_terminal.subject_label') }}
            </span>
            <span class="min-w-0 truncate font-mono text-xs text-day-ink dark:text-night-ink">
              {{ record.subject }}
            </span>
          </div>

          <!-- Secret Link Hero -->
          <section
            class="relative"
            aria-labelledby="secret-header"
            data-testid="receipt-secret-link">
            <h1
              id="secret-header"
              class="sr-only">
              {{ t('web.LABELS.secret_link') }}
            </h1>

            <div
              v-if="showHero"
              class="p-4 sm:p-6">
              <div class="mb-3 flex items-center gap-2">
                <span
                  class="inline-block size-1.5 bg-brand-500 shadow-[0_0_6px_var(--color-brand-500)]"
                  aria-hidden="true"></span>
                <!-- prettier-ignore-attribute class -->
                <span
                  class="font-mono text-[10px] tracking-[0.08em] text-day-ink-faint
                  uppercase dark:text-night-ink-faint">
                  {{ t('receipt_terminal.hero_label') }}
                </span>
              </div>

              <!-- prettier-ignore-attribute class -->
              <button
                type="button"
                data-testid="receipt-link-hero"
                :aria-label="t('receipt_terminal.copy_link_aria')"
                @click="copyLink"
                class="relative block w-full border bg-day p-4 pb-9 text-left
                transition-colors duration-200 sm:p-5 sm:pb-10 dark:bg-night-100"
                :class="
                  linkCopied
                    ? 'border-emerald-500/60'
                    : `border-day-line hover:border-brand-500/40
                      dark:border-white/[0.13] dark:hover:border-brand-500/40`
                ">
                <!-- prettier-ignore-attribute class -->
                <span
                  class="font-mono text-[clamp(13px,2vw,17px)] leading-relaxed break-all
                  text-brand-600 dark:text-brand-500">
                  {{ shareUrl }}
                </span>
                <span
                  class="pointer-events-none absolute right-3.5 bottom-2.5 font-mono text-[9px] tracking-[0.08em] uppercase"
                  :class="
                    linkCopied
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-day-ink-faint dark:text-night-ink-faint'
                  "
                  aria-live="polite">
                  {{ linkCopied ? t('receipt_terminal.copied') : t('receipt_terminal.click_to_copy') }}
                </span>
              </button>

              <p class="mt-3 font-mono text-[10px] leading-relaxed text-day-ink-faint dark:text-night-ink-faint">
                {{ t('receipt_terminal.single_use_note') }}
              </p>

              <!-- Copy-now warning when the link carries an E2EE attachment: the
                   key lives only in the fragment, so it can't be recovered later. -->
              <p
                v-if="attachmentFragment"
                class="mt-2 flex items-center gap-1.5 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                data-testid="receipt-attach-copy-now">
                <svg
                  class="size-4 shrink-0"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true">
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
                {{ t('web.secrets.attach_copy_now') }}
              </p>
            </div>

            <!-- Recipient flow (or missing share_url): keep the original SecretLink -->
            <div
              v-else-if="isAvailable"
              class="p-4 sm:p-6">
              <SecretLink
                :record="record"
                :details="details"
                :is-initial-view="!record.is_previewed" />
            </div>
          </section>

          <!-- Share via + expiry ring -->
          <section
            v-if="showHero"
            class="border-t border-day-line p-4 sm:p-6 dark:border-white/[0.07]"
            data-testid="receipt-share-channels">
            <div class="grid grid-cols-1 gap-6 sm:grid-cols-[1fr_auto] sm:items-center">
              <div class="min-w-0">
                <!-- prettier-ignore-attribute class -->
                <div
                  class="mb-3 font-mono text-[10px] tracking-[0.08em] text-day-ink-faint
                  uppercase dark:text-night-ink-faint">
                  {{ t('receipt_terminal.share_via') }}
                </div>
                <ShareChannels
                  :link="shareUrl"
                  :notify-identifier="record.identifier"
                  :can-notify="isAuthenticated" />
              </div>
              <ExpiryRing
                v-if="createdAt && expiresAt"
                :created-at="createdAt"
                :expires-at="expiresAt" />
            </div>
          </section>

          <!-- Metadata strip -->
          <!-- prettier-ignore-attribute class -->
          <section
            v-if="isAvailable && metaCells.length"
            data-testid="receipt-metadata"
            class="grid grid-cols-2 gap-px border-t border-day-line bg-gray-200
            sm:grid-cols-4 dark:border-white/[0.07] dark:bg-white/[0.07]">
            <div
              v-for="cell in metaCells"
              :key="cell.key"
              class="bg-day p-3.5 dark:bg-night-100">
              <!-- prettier-ignore-attribute class -->
              <div
                class="mb-1 font-mono text-[9px] tracking-[0.08em] text-day-ink-faint
                uppercase dark:text-night-ink-faint">
                {{ cell.label }}
              </div>
              <div class="font-mono text-xs break-words text-day-ink dark:text-night-ink">
                {{ cell.value }}
              </div>
            </div>
          </section>

          <!-- Recipient + Memo Section -->
          <div
            v-if="details.show_recipients || record.memo"
            class="border-t border-day-line p-4 sm:p-6 dark:border-white/[0.07]">
            <!-- Recipient (primary): who this secret is for -->
            <h3
              v-if="details.show_recipients"
              class="flex items-center text-lg font-semibold text-day-ink dark:text-night-ink">
              <OIcon
                collection="material-symbols"
                name="mail-outline"
                class="mr-2 size-5 text-brand-500 dark:text-brand-400" />
              <span class="mr-2 text-day-ink-faint dark:text-night-ink-faint">{{ t('web.COMMON.sent_to') }}</span>
              <span class="break-words">{{ record.recipient_name || record.recipients }}</span>
            </h3>

            <!-- Memo (secondary): supporting context -->
            <p
              v-if="record.memo"
              :class="[
                'flex items-start text-sm text-day-ink-muted dark:text-night-ink-muted',
                details.show_recipients ? 'mt-2 pl-7' : '',
              ]">
              <OIcon
                v-if="!details.show_recipients"
                collection="material-symbols"
                name="notes"
                class="mt-0.5 mr-2 size-4 shrink-0 text-gray-400 dark:text-night-ink-faint" />
              <span class="mr-1 text-day-ink-faint dark:text-night-ink-faint">{{ t('incoming.memo_label') }}:</span>
              <span class="break-words">{{ record.memo }}</span>
            </p>
          </div>

          <!-- Secret Value -->
          <!-- prettier-ignore-attribute class -->
          <section
            v-if="details.show_secret && details.secret_value"
            class="border-t border-day-line bg-day-100 p-4
            sm:p-6 dark:border-white/[0.07] dark:bg-night-200/60">
            <!-- prettier-ignore-attribute class -->
            <textarea
              readonly
              :value="details.secret_value"
              :rows="details.display_lines || 3"
              class="w-full resize-none border
              border-day-line bg-day px-4 py-3 font-mono
              text-base leading-tight tracking-wide whitespace-pre text-day-ink transition-colors
              focus:border-brand-500 focus:ring-2 focus:ring-brand-500
              dark:border-white/[0.13] dark:bg-night dark:text-night-ink dark:focus:border-brand-400"></textarea>
            <div class="mt-3 flex w-full items-center justify-between">
              <p
                ref="warningMessage"
                class="flex items-center gap-2 text-sm"
                role="alert"
                aria-live="polite"
                :class="{ invisible: !showWarning }">
                <OIcon
                  collection="material-symbols"
                  name="warning"
                  class="size-4 shrink-0 text-amber-600 dark:text-amber-400"
                  aria-hidden="true" />
                {{ t('web.receipt.only_see_once') }}
              </p>
              <CopyButton
                v-if="details.secret_value"
                class="ml-auto"
                :text="details.secret_value" />
            </div>
          </section>

          <!-- Encrypted Content Placeholder -->
          <!-- prettier-ignore-attribute class -->
          <section
            v-if="(!details.show_secret || !details.secret_value) && !record.is_destroyed"
            class="border-t border-day-line bg-gray-100 p-4
            sm:p-6 dark:border-white/[0.07] dark:bg-night-200/60">
            <!-- prettier-ignore-attribute class -->
            <div
              class="flex items-center justify-between py-2 font-mono
              text-gray-400 dark:text-night-ink-faint">
              <div class="flex flex-1 items-center">
                <span class="inline-block w-full overflow-hidden">
                  <span class="blur-sm select-none">•••••••••••••••••••••••••••••••••••••••</span>
                </span>
                <OIcon
                  collection="material-symbols"
                  name="lock-outline"
                  class="ml-2 size-4 shrink-0 text-gray-400" />
              </div>
              <!-- prettier-ignore-attribute class -->
              <span
                class="font-mono text-[9px] tracking-[0.08em] text-day-ink-muted
                uppercase dark:text-night-ink-muted">
                {{ t('web.LABELS.encrypted') }}
              </span>
            </div>
          </section>

          <!-- Status & Timeline -->
          <section
            class="border-t border-day-line p-4 sm:p-6 dark:border-white/[0.07]"
            aria-labelledby="section-status"
            data-testid="receipt-status">
            <div class="mb-3 flex items-center justify-between">
              <!-- prettier-ignore-attribute class -->
              <h2
                id="section-status"
                class="flex items-center font-mono text-[10px] tracking-[0.08em] text-day-ink-muted
                uppercase dark:text-night-ink-muted">
                <OIcon
                  collection="material-symbols"
                  name="history"
                  class="mr-2"
                  size="4" />
                {{ t('web.LABELS.timeline') }}
              </h2>
              <StatusBadge
                :record="record"
                :expires-in="details?.secret_realttl ?? undefined" />
            </div>

            <TimelineDisplay
              :record="record"
              :details="details" />
          </section>

          <!-- Destruction zone -->
          <!-- prettier-ignore-attribute class -->
          <section
            v-if="isAvailable && showBurn"
            class="border-t border-day-line bg-day-100 p-4
            sm:p-6 dark:border-white/[0.07] dark:bg-night-200/60"
            aria-labelledby="section-actions">
            <h2
              id="section-actions"
              class="sr-only">
              {{ t('web.LABELS.actions') }}
            </h2>

            <div class="mb-4 text-center">
              <!-- prettier-ignore-attribute class -->
              <p
                class="font-mono text-[10px] tracking-[0.08em] text-day-ink-muted
                uppercase dark:text-night-ink-muted">
                {{ t('receipt_terminal.burn_prompt') }}
              </p>
              <p class="mt-1 font-mono text-[10px] text-day-ink-faint dark:text-night-ink-faint">
                {{ t('receipt_terminal.burn_note') }}
              </p>
            </div>

            <BurnButtonForm
              :record="record"
              :details="details" />
          </section>

          <CornerBrackets />
        </div>

        <!-- Help Section -->
        <!-- prettier-ignore-attribute class -->
        <section
          v-if="isAvailable && helpEnabled"
          aria-labelledby="section-help"
          class="relative border border-day-line bg-day p-4
          sm:p-6 dark:border-white/[0.07] dark:bg-night-100">
          <NeedHelpModal>
            <template #content>
              <ReceiptFAQ
                :record="record"
                :details="details" />
            </template>
          </NeedHelpModal>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
